import { FormEvent, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import { HttpStatusCode } from "axios";
import Input from "../input/Input";
import SocialLoginButtons from "../social-login/SocialLoginButtons";
import TrialCodeStep from "./TrialCodeStep";
import { useAppDispatch } from "../../app/hooks";
import { startTrialAsync } from "../../features/authentication/authenticationApi";
import { getRecaptchaToken, loadRecaptcha, TRIAL_START_ACTION } from "../../features/authentication/recaptcha";
import { getPendingMetaClickId } from "../../features/attribution/metaClickCapture";
import { useSocialLogin } from "../../features/authorization/useSocialLogin";
import { ROUTES } from "../../routes";
import { validateEmail } from "./authValidation";

interface RegisterFormProps {
  onSignIn: () => void;
}

/** The CTA placement carried in from the marketing site, else this screen itself. */
const PLACEMENT_PATTERN = /^[a-z0-9][a-z0-9-]{0,63}$/;
const DEFAULT_PLACEMENT = "register";

const START_ERROR_KEYS: Partial<Record<number, string>> = {
  [HttpStatusCode.BadRequest]: "auth.errors.invalidEmailFormat",
  [HttpStatusCode.Forbidden]: "auth.trial.recaptchaFailed",
  [HttpStatusCode.TooManyRequests]: "auth.trial.tooManyRequests",
};

/**
 * The sign-up form, email-first: one address is all it takes. The API creates the
 * account and mails a single-use link + code; the visitor is in after clicking the
 * link or typing the code here. No password, no name yet (asked after the first
 * sign-in), and the legal documents are accepted by starting — the line under the
 * button says so. The one-tap providers stay as the secondary path.
 */
const RegisterForm = ({ onSignIn }: RegisterFormProps) => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const { socialLoading, socialError, loginWithProvider } = useSocialLogin();

  const [step, setStep] = useState<"email" | "code">(searchParams.get("step") === "code" ? "code" : "email");
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadRecaptcha();
  }, []);

  const busy = loading || socialLoading;
  const displayError = error ?? socialError;

  const placement = (): string => {
    const campaign = searchParams.get("utm_campaign")?.toLowerCase() ?? "";
    return PLACEMENT_PATTERN.test(campaign) ? campaign : DEFAULT_PLACEMENT;
  };

  const handleStart = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setEmailError(null);

    const trimmed = email.trim();
    if (trimmed === "") {
      setEmailError(t("auth.errors.emailRequired"));
      return;
    }
    if (!validateEmail(trimmed)) {
      setEmailError(t("auth.errors.invalidEmailFormat"));
      return;
    }

    setLoading(true);
    const recaptchaToken = await getRecaptchaToken(TRIAL_START_ACTION);
    const result = await dispatch(
      startTrialAsync({
        email: trimmed,
        // The sign-up language becomes the instructor's language, so the welcome
        // email already speaks it.
        languageCode: i18n.language.split("-")[0],
        placement: placement(),
        metaClickId: getPendingMetaClickId() ?? undefined,
        recaptchaToken: recaptchaToken || undefined,
      })
    );
    setLoading(false);

    if (startTrialAsync.fulfilled.match(result)) {
      setEmail(trimmed);
      setStep("code");
    } else {
      setError(t(START_ERROR_KEYS[result.payload?.status ?? 0] ?? "auth.errors.unknownError"));
    }
  };

  if (step === "code") {
    return <TrialCodeStep email={email} onChangeEmail={() => setStep("email")} />;
  }

  return (
    <form onSubmit={handleStart} noValidate>
      <h2 className="text-grey-20 dark:text-white">{t("auth.trial.title")}</h2>
      <p className="mt-2 text-grey-50 dark:text-grey-70">{t("auth.trial.subtitle")}</p>

      <div className="mt-6">
        <Input
          label={t("auth.email")}
          type="email"
          name="trialEmail"
          id="trialEmail"
          autoComplete="email"
          fullWidth
          margin="dense"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (emailError) setEmailError(null);
          }}
          disabled={busy}
          error={!!emailError}
          helperText={emailError}
          labelPosition="top-left"
        />
      </div>

      {displayError && (
        <p className="mb-3 text-sm p-2 rounded-lg text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
          {displayError}
        </p>
      )}

      <button
        type="submit"
        className="btn btn-primary w-full h-[46px] mt-2 flex items-center justify-center text-[16px] no-underline"
        disabled={busy}
      >
        {t("auth.trial.start")}
      </button>
      <p className="mt-2 text-center text-xs text-grey-50 dark:text-grey-60">{t("auth.trial.noPasswordHint")}</p>
      <p className="mt-3 text-center text-xs text-grey-50 dark:text-grey-60">
        <Trans
          i18nKey="auth.trial.termsByStarting"
          components={{
            termsLink: (
              <Link
                to={ROUTES.TERMS_OF_SERVICE}
                target="_blank"
                className="text-primary-40 dark:text-primary-90 hover:underline"
              />
            ),
            privacyLink: (
              <Link
                to={ROUTES.PRIVACY_POLICY}
                target="_blank"
                className="text-primary-40 dark:text-primary-90 hover:underline"
              />
            ),
          }}
        />
      </p>

      {/* The reCAPTCHA badge is hidden app-wide (input.css), so Google's attribution goes here. */}
      <p className="mt-2 text-center text-[11px] text-grey-50 dark:text-grey-60">
        <Trans
          i18nKey="auth.trial.recaptchaNotice"
          components={{
            privacyLink: (
              <a
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:opacity-80"
              />
            ),
            termsLink: (
              <a
                href="https://policies.google.com/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:opacity-80"
              />
            ),
          }}
        />
      </p>

      <div className="mt-6">
        <SocialLoginButtons onProvider={loginWithProvider} loading={busy} divider="above" />
      </div>

      <div className="mt-4 text-center">
        <p className="text-sm">
          {t("auth.alreadyHaveAccount")}{" "}
          <span
            className="cursor-pointer hover:underline text-primary-40 dark:text-primary-90 font-medium"
            onClick={onSignIn}
          >
            {t("auth.signIn")}
          </span>
        </p>
      </div>
    </form>
  );
};

export default RegisterForm;
